import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listImagesForMigration, proxyFetchImage, migrateImage, type ImageItem } from "@/lib/image-migration.functions";
import { toWebp } from "@/lib/image-webp";

export const Route = createFileRoute("/_authenticated/migracija-slik")({
  head: () => ({ meta: [{ title: "Migracija slik v WebP — Admin" }] }),
  component: Page,
});

type LogEntry = { label: string; status: "ok" | "err" | "skip"; message?: string };

function Page() {
  const fetchList = useServerFn(listImagesForMigration);
  const fetchProxy = useServerFn(proxyFetchImage);
  const runMigrate = useServerFn(migrateImage);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["image-migration-list"],
    queryFn: () => fetchList(),
  });

  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [log, setLog] = useState<LogEntry[]>([]);

  async function blobToBase64(blob: Blob): Promise<string> {
    const buf = new Uint8Array(await blob.arrayBuffer());
    let bin = "";
    const chunk = 0x8000;
    for (let i = 0; i < buf.length; i += chunk) {
      bin += String.fromCharCode(...buf.subarray(i, i + chunk));
    }
    return btoa(bin);
  }

  function base64ToBlob(base64: string, contentType: string): Blob {
    const bin = atob(base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: contentType });
  }

  async function processOne(item: ImageItem): Promise<LogEntry> {
    try {
      const { base64, contentType } = await fetchProxy({ data: { url: item.url } });
      if (!contentType.startsWith("image/")) return { label: item.label, status: "skip", message: `Ni slika (${contentType})` };
      if (contentType === "image/svg+xml") return { label: item.label, status: "skip", message: "SVG — ne pretvarjamo" };

      const blob = base64ToBlob(base64, contentType);
      const file = new File([blob], "image", { type: contentType });
      const webp = await toWebp(file, { maxDim: 1920, quality: 0.85 });
      if (!webp) return { label: item.label, status: "skip", message: "Pretvorba ni uspela" };

      const webpBase64 = await blobToBase64(webp);
      const res = await runMigrate({ data: { item, webpBase64 } });
      return { label: item.label, status: "ok", message: res.newUrl };
    } catch (e) {
      return { label: item.label, status: "err", message: (e as Error).message };
    }
  }

  async function startMigration() {
    if (!data?.items.length) return;
    setRunning(true);
    setDone(0);
    setLog([]);
    for (const item of data.items) {
      const entry = await processOne(item);
      setLog((prev) => [...prev, entry]);
      setDone((d) => d + 1);
    }
    setRunning(false);
    await refetch();
  }

  if (isLoading) return <div className="mx-auto max-w-4xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  if (error) return <div className="mx-auto max-w-4xl px-6 py-24 text-destructive">Napaka: {(error as Error).message}</div>;
  const items = data?.items ?? [];

  return (
    <section className="mx-auto max-w-4xl px-6 py-16">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Skrbniško orodje</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3">Migracija slik v WebP</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Prenese vse obstoječe slike izdelkov, trgovin in profilov, jih pretvori v WebP (max 1920px, kakovost 0.85),
            naloži v Lovable Cloud shrambo in posodobi povezave v bazi. Stare datoteke v shrambi se izbrišejo, zunanje URL-je le nadomestimo.
          </p>
        </div>
        <Link to="/admin" className="border border-border px-4 py-2 text-sm hover:bg-muted whitespace-nowrap">← Admin</Link>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button
          type="button"
          onClick={startMigration}
          disabled={running || items.length === 0}
          className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90 disabled:opacity-40"
        >
          {running ? `Pretvarjam… ${done}/${items.length}` : items.length === 0 ? "Vse je že WebP ✓" : `Začni pretvorbo (${items.length})`}
        </button>
        {!running && items.length > 0 && (
          <span className="text-sm text-muted-foreground">{items.length} slik še ni v WebP formatu.</span>
        )}
      </div>

      {running && (
        <div className="mt-6 h-2 bg-muted overflow-hidden">
          <div className="h-full bg-accent transition-all" style={{ width: `${(done / items.length) * 100}%` }} />
        </div>
      )}

      {log.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-xl">Dnevnik</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {log.map((e, i) => (
              <li key={i} className="flex items-start gap-3 border-b border-border pb-2">
                <span className={`mt-0.5 inline-block w-16 text-xs font-medium ${e.status === "ok" ? "text-emerald-600" : e.status === "err" ? "text-destructive" : "text-muted-foreground"}`}>
                  {e.status === "ok" ? "✓ OK" : e.status === "err" ? "✗ Napaka" : "— Preskočeno"}
                </span>
                <span className="flex-1">
                  <div>{e.label}</div>
                  {e.message && <div className="text-xs text-muted-foreground break-all">{e.message}</div>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!running && log.length === 0 && items.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-xl">Najdene slike</h2>
          <ul className="mt-4 space-y-1 text-sm text-muted-foreground max-h-96 overflow-y-auto border border-border p-4">
            {items.slice(0, 200).map((it, i) => (
              <li key={i} className="truncate">{it.label}</li>
            ))}
            {items.length > 200 && <li>… in še {items.length - 200}</li>}
          </ul>
        </div>
      )}
    </section>
  );
}
